import { Alert, Anchor, Button, Paper, PasswordInput, Stack, Text, TextInput, Title } from '@mantine/core';
import { useForm } from '@mantine/form';
import { useState, type ReactNode } from 'react';
import { Link, Navigate, useLocation, useNavigate, useSearchParams } from 'react-router';
import { api, ApiError, errorMessage } from '../../api/client';
import { useAuth } from '../../auth/AuthProvider';
import { homePath } from '../../layout/nav';
import { Wordmark } from '../../layout/Shell';

function AuthCard({ title, subtitle, children }: { title: string; subtitle?: string; children: ReactNode }) {
  return (
    <div className="sb-auth-ground">
      <Stack w="100%" maw={400} gap="lg">
        <Stack gap={2} align="center">
          <Wordmark variant="full" />
          <Text size="xs" c="dimmed" tt="uppercase" fw={600} style={{ letterSpacing: '0.1em' }}>
            Back office
          </Text>
        </Stack>
        <Paper withBorder shadow="sm" p="xl" radius="md">
          <Stack gap="md">
            <Stack gap={4}>
              <Title order={3}>{title}</Title>
              {subtitle && (
                <Text size="sm" c="dimmed">
                  {subtitle}
                </Text>
              )}
            </Stack>
            {children}
          </Stack>
        </Paper>
      </Stack>
    </div>
  );
}

export function LoginPage() {
  const { state, user, login } = useAuth();
  const location = useLocation();
  const [error, setError] = useState<string | null>(null);
  const form = useForm({ initialValues: { email: '', password: '' } });

  if (user) {
    const from = (location.state as { from?: string } | null)?.from;
    return <Navigate to={from && from !== '/login' ? from : homePath(user.permissions)} replace />;
  }

  return (
    <AuthCard title="Sign in" subtitle="For the The Success Time newsroom and admins.">
      <form
        onSubmit={form.onSubmit(async values => {
          setError(null);
          try {
            await login(values.email.trim(), values.password);
          } catch (e) {
            setError(e instanceof ApiError && e.code === 'INVALID_CREDENTIALS' ? 'The email or password is wrong.' : errorMessage(e));
          }
        })}
      >
        <Stack gap="sm">
          {error && <Alert color="red">{error}</Alert>}
          <TextInput label="Email" type="email" autoComplete="username" required {...form.getInputProps('email')} />
          <PasswordInput label="Password" autoComplete="current-password" required {...form.getInputProps('password')} />
          <Button type="submit" loading={form.submitting} disabled={state.status === 'loading'} fullWidth mt="xs">
            Sign in
          </Button>
          <Anchor component={Link} to="/forgot-password" size="sm" ta="center">
            Forgot your password?
          </Anchor>
        </Stack>
      </form>
    </AuthCard>
  );
}

export function ForgotPasswordPage() {
  const [sent, setSent] = useState(false);
  const form = useForm({ initialValues: { email: '' } });
  return (
    <AuthCard title="Reset your password" subtitle="We'll email a link that works for one hour.">
      {sent ? (
        <Alert color="green">If that email belongs to a staff account, a reset link is on its way. Check your inbox.</Alert>
      ) : (
        <form
          onSubmit={form.onSubmit(async values => {
            await api('/auth/forgot-password', { method: 'POST', body: { email: values.email.trim() } }).catch(() => undefined);
            setSent(true);
          })}
        >
          <Stack gap="sm">
            <TextInput label="Email" type="email" required {...form.getInputProps('email')} />
            <Button type="submit" loading={form.submitting} fullWidth>
              Send the link
            </Button>
          </Stack>
        </form>
      )}
      <Anchor component={Link} to="/login" size="sm" ta="center">
        Back to sign in
      </Anchor>
    </AuthCard>
  );
}

const passwordRules = {
  password: (value: string) => (value.length < 10 ? 'Use at least 10 characters' : null),
  confirm: (value: string, values: { password: string }) => (value !== values.password ? 'The two passwords are different' : null),
};

/** Signs in with the token that accept-invite / reset-password answer with, else goes to sign-in. */
async function finish(result: unknown, adopt: (token: string) => Promise<void>, navigate: (to: string) => void) {
  const token = (result as { data?: { accessToken?: string } } | undefined)?.data?.accessToken;
  if (token) {
    await adopt(token);
    navigate('/');
  } else {
    navigate('/login');
  }
}

export function ResetPasswordPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { adopt } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const form = useForm({ initialValues: { password: '', confirm: '' }, validate: passwordRules });
  const token = params.get('token') ?? '';
  return (
    <AuthCard title="Choose a new password" subtitle="At least 10 characters. Every other session is signed out.">
      <form
        onSubmit={form.onSubmit(async values => {
          setError(null);
          try {
            const result = await api('/auth/reset-password', { method: 'POST', body: { token, newPassword: values.password } });
            await finish(result, adopt, navigate);
          } catch (e) {
            setError(errorMessage(e));
          }
        })}
      >
        <Stack gap="sm">
          {!token && <Alert color="red">This link is incomplete. Open it from the email again.</Alert>}
          {error && <Alert color="red">{error}</Alert>}
          <PasswordInput label="New password" autoComplete="new-password" {...form.getInputProps('password')} />
          <PasswordInput label="Type it again" autoComplete="new-password" {...form.getInputProps('confirm')} />
          <Button type="submit" loading={form.submitting} disabled={!token} fullWidth>
            Save the password
          </Button>
        </Stack>
      </form>
    </AuthCard>
  );
}

export function AcceptInvitePage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { adopt } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const form = useForm({
    initialValues: { name: '', password: '', confirm: '' },
    validate: { ...passwordRules, name: (value: string) => (value.trim().length < 2 ? 'Your name, as colleagues know it' : null) },
  });
  const token = params.get('token') ?? '';
  return (
    <AuthCard title="Join the back office" subtitle="You were invited to The Success Time. Set your name and a password.">
      <form
        onSubmit={form.onSubmit(async values => {
          setError(null);
          try {
            const result = await api('/auth/accept-invite', { method: 'POST', body: { token, name: values.name.trim(), password: values.password } });
            await finish(result, adopt, navigate);
          } catch (e) {
            setError(errorMessage(e));
          }
        })}
      >
        <Stack gap="sm">
          {!token && <Alert color="red">This link is incomplete. Open it from the invite email again.</Alert>}
          {error && <Alert color="red">{error}</Alert>}
          <TextInput label="Your name" description="Shown as the byline on your stories" {...form.getInputProps('name')} />
          <PasswordInput label="Password" autoComplete="new-password" description="At least 10 characters" {...form.getInputProps('password')} />
          <PasswordInput label="Type it again" autoComplete="new-password" {...form.getInputProps('confirm')} />
          <Button type="submit" loading={form.submitting} disabled={!token} fullWidth>
            Join
          </Button>
        </Stack>
      </form>
    </AuthCard>
  );
}
